import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import HotelCardImageList from './HotelCardImage/HotelCardImageList';
import HotelName from './HotelCardInfoBlocks/HotelName';
import HotelRating from './HotelCardInfoBlocks/HotelRating';
import HotelPriceInfo from './HotelPriceInfo';
import RegimenInfo from './HotelCardInfoBlocks/RegimenInfo';
import TarifaInfo from './HotelCardInfoBlocks/TarifaInfo';
import DateInfo from './HotelCardInfoBlocks/DateInfo';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getCheapestRate } from '@shared/helpers';

const getNights = (checkIn, checkOut) => checkOut.diff(checkIn, 'days');

const HotelCardList = ({
  hotel,
  hasIdFav,
  isLoadImage,
  setIsLoadImage,
  getFavConfig,
  handleImageError,
  defaultImage,
  handleChangePage,
  provider,
  integration,
  handleMouseEnter,
  setUsdUserTooltip,
  handleMouseLeave,
  business_types,
  compare,
  usdUserTooltip,
  isFromMap = false,
  isMobile = false,
  handleCloseModal,
  searchInfo,
  user,
  onClickAddFavorite,
  onClickOmit,
  onClickDeleteFavorite,
  isAdmin,
  onMoveMap,
}) => {
  const checkIn = moment(searchInfo.checkIn);
  const checkOut = moment(searchInfo.checkOut);
  const nights = getNights(checkIn, checkOut);

  const theme = useTheme();

  const cheapestRate = getCheapestRate(hotel.rates);
  const isNonRefundable = !cheapestRate?.cancel_cost?.[0]?.CanBeCanceled;
  const boardDescription = cheapestRate?.board?.description || '';
  const passengersCount = searchInfo?.passengers?.length || 0;

  const idIntegration = hotel?.id_integration;

  return (
    <Card
      sx={{
        width: '100%',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'column' },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        overflow: { xs: 'hidden', sm: 'visible' },
        borderRadius: '8px 8px 0 0 !important',
        maxWidth: { xs: '300px', sm: '400px' },
        maxHeight: { xs: '652px', sm: '700px' },
        my: { xs: '20px', sm: '15px' },
      }}
      onClick={handleChangePage}
      id={`HOTELCARD-${hotel.id_hotel}`}
    >
      <HotelCardImageList
        hotel={hotel}
        hasIdFav={hasIdFav}
        isLoadImage={isLoadImage}
        setIsLoadImage={setIsLoadImage}
        getFavConfig={getFavConfig}
        handleImageError={handleImageError}
        defaultImage={defaultImage}
        isFromMap={isFromMap}
        handleCloseModal={handleCloseModal}
        user={user}
        onClickAddFavorite={onClickAddFavorite}
        onClickOmit={onClickOmit}
        onClickDeleteFavorite={onClickDeleteFavorite}
        isAdmin={isAdmin}
      />
      <CardContent
        sx={{
          p: '8px 12px',
          '&:last-child': { pb: '16px' },
        }}
      >
        <Grid
          container
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
            minHeight: 50,
            flexWrap: 'nowrap',
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <HotelName name={hotel.name} isFromMap={isFromMap} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <HotelRating rating={hotel.rating} isFromMap={isFromMap} />

            {hotel?.location?.latitude && (
              <IconButton
                onClick={event => {
                  event.stopPropagation();

                  onMoveMap({
                    lat: hotel.location.latitude,
                    lng: hotel.location.longitude,
                  });
                }}
                sx={{
                  border: `1px dashed ${theme.tertiary}`,
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  p: 0,
                  transition: 'all .2s ease',
                  '&:hover': {
                    backgroundColor: `${theme.tertiary}15`,
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <LocationOnIcon
                  sx={{
                    fontSize: 22,
                    color: theme.tertiary,
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Grid>
        <HotelPriceInfo
          hotel={hotel}
          rate={cheapestRate}
          provider={provider}
          integration={integration}
          handleMouseEnter={handleMouseEnter}
          setUsdUserTooltip={setUsdUserTooltip}
          handleMouseLeave={handleMouseLeave}
          business_types={business_types}
          compare={compare}
          usdUserTooltip={usdUserTooltip}
          isMobile={isMobile}
          isFromMap={isFromMap}
        />
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <DateInfo
              checkIn={checkIn}
              checkOut={checkOut}
              nights={nights}
              passengersCount={passengersCount}
            />
          </Grid>

          <Grid item xs={12}>
            <RegimenInfo
              boardDescription={boardDescription}
              rate={cheapestRate}
            />
          </Grid>

          <Grid item xs={12}>
            <TarifaInfo
              isNonRefundable={isNonRefundable}
              idIntegration={idIntegration}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default HotelCardList;
